import { Backdrop, CircularProgress } from '@mui/material';
import React, { useState, useEffect, useRef } from 'react';
import { FiArrowLeft, FiCheck } from 'react-icons/fi';
import { useSelector } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '../../api/api';
import ToastMessage from '../../utils/muitoast';
import { draftToFormRow } from '../draftTransformers';
import { useFormDraft } from '../useFormDraft';

// ─── Reusable Components (defined OUTSIDE to prevent focus loss) ───

const InputField = ({ label, placeholder, value, onChange, required, type = 'text' }) => (
  <div className="flex flex-col gap-1">
    <label className="text-sm text-start font-medium text-gray-700">
      {label} {required && <span className="text-red-500">*</span>}
    </label>
    <input
      type={type}
      placeholder={placeholder}
      value={value || ''}
      onChange={onChange}
      className="border bg-[#F3F3F5] rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-400"
    />
  </div>
);

const RadioGroup = ({ label, name, options, value, onChange, required, questionNumber }) => (
  <div className="py-4 border-b border-gray-200">
    <div className="flex items-start gap-3">
      <span className="bg-[#18181B] text-white text-xs font-bold w-7 h-7 flex items-center justify-center rounded-full mt-0.5 shrink-0">
        {questionNumber}
      </span>
      <div className="flex flex-col gap-2">
        <span className="text-sm text-left font-medium text-gray-800">
          {label} {required && <span className="text-red-500">*</span>}
        </span>
        <div className="flex gap-6">
          {options.map((opt) => (
            <label key={opt} className="flex items-center gap-2 cursor-pointer text-sm text-gray-700">
              <input
                type="radio"
                name={name}
                value={opt}
                checked={value === opt}
                onChange={(e) => onChange(e.target.value)}
                className="accent-black w-4 h-4"
              />
              {opt}
            </label>
          ))}
        </div>
      </div>
    </div>
  </div>
);

// ─── Main Component ───

const TrailerInspectionForm = () => {
  const [currentStage, setCurrentStage] = useState(1);
  const totalStages = 5;
  const navigate = useNavigate();
  const user = useSelector((state) => state.user.userDetails);
  const [loader, setLoader] = useState(false);
  const [severity, setSeverity] = useState('success');
  const [open, setOpen] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');
  const [searchParams] = useSearchParams();
  const mode = searchParams.get('mode');
  const id = searchParams.get('id');

  // Dropdown states
  const [companyTabs, setCompanyTabs] = useState([]);
  const [selectedCompanyId, setSelectedCompanyId] = useState(null);
  const [employeeSuggestions, setEmployeeSuggestions] = useState([]);
  const [showEmpSuggestions, setShowEmpSuggestions] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedJobName, setSelectedJobName] = useState('');
  const [hoveredIndex, setHoveredIndex] = useState(-1);
  const jobInputRef = useRef(null);
  const empInputRef = useRef(null);

  const [formData, setFormData] = useState({
    // Assignment
    companyId: null,
    empId: null,
    personAssigned: '',
    trailerUnit: '',
    lastKnownJobNumber: '',
    trailerType: '',

    // Compliance
    licenseRegistration: '',
    insuranceCard: '',
    ballSizeMatch: '',
    fireExtinguisher: '',

    // Condition
    tireCondition: '',
    axelCondition: '',
    brakeCondition: '',
    jackSafetyChain: '',

    // Lights & Safety
    turnSignalLights: '',
    brakeLights: '',
    properTieDowns: '',
    trailerHitchDoorLocks: '',

    // Comments
    comments: '',
  });

  const stages = [
    { label: 'Assignment', number: 1 },
    { label: 'Compliance', number: 2 },
    { label: 'Condition', number: 3 },
    { label: 'Lights & Safety', number: 4 },
    { label: 'Comments', number: 5 },
  ];

  const handleCloseToast = (event, reason) => {
    if (reason === 'clickaway') return;
    setOpen(false);
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  // ─── Fetch company tabs ───
  const fetchTabsData = async () => {
    try {
      setLoader(true);
      const response = await api.get(`/v1/timesheets/company-details`);
      if (response.status === 200) {
        setCompanyTabs(response.data);
        setSelectedCompanyId(response.data[0]?.id);
      }
    } catch (error) {
      console.error('Error fetching company data:', error);
    } finally {
      setLoader(false);
    }
  };

  useEffect(() => {
    fetchTabsData();
  }, []);

  // ─── Click outside to close dropdowns ───
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (jobInputRef.current && !jobInputRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
      if (empInputRef.current && !empInputRef.current.contains(event.target)) {
        setShowEmpSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // ─── Employee search ───
  const fetchEmployeeSuggestions = async (query) => {
    try {
      if (!query) {
        setEmployeeSuggestions([]);
        setShowEmpSuggestions(false);
        return;
      }
      const response = await api.get('/v1/timesheets/get-employee', {
        params: { company_id: selectedCompanyId, empid: query },
      });
      if (response.status === 200 && Array.isArray(response.data)) {
        setEmployeeSuggestions(response.data);
        setShowEmpSuggestions(true);
      } else {
        setEmployeeSuggestions([]);
        setShowEmpSuggestions(false);
      }
    } catch (error) {
      console.error('Error fetching employee suggestions:', error);
    }
  };

  const handleEmployeeSelect = (emp) => {
    setFormData((prev) => ({
      ...prev,
      personAssigned: `${emp.firstname} ${emp.lastname}`,
      empId: emp.empid,
    }));
    setShowEmpSuggestions(false);
    setEmployeeSuggestions([]);
  };

  // ─── Job search ───
  const fetchJobSuggestions = async (query) => {
    try {
      if (!query) {
        setSuggestions([]);
        setShowSuggestions(false);
        setSelectedJobName('');
        return;
      }
      if (/^0{1,4}$/.test(query)) {
        setSuggestions([{ job_num: '0000', job_name: 'Overhead' }]);
        setShowSuggestions(true);
        return;
      }
      const response = await api.get('/v1/timesheets/get-jobs', {
        params: { company_id: selectedCompanyId, job_num: query },
      });
      if (response.status === 200) {
        setSuggestions(response.data || []);
        setShowSuggestions(true);
      } else {
        setSuggestions([]);
        setShowSuggestions(false);
      }
    } catch (error) {
      console.error('Error fetching job suggestions:', error);
    }
  };

  const handleJobCodeSelect = (code) => {
    setSelectedJobName(code.job_name);
    setSuggestions([]);
    setShowSuggestions(false);
    setFormData((prev) => ({
      ...prev,
      lastKnownJobNumber: code.job_num,
    }));
  };

  // ─── Edit mode: load existing data ───
  const draftId = searchParams.get('draft_id');
  useEffect(() => {
    if (mode === 'edit' && id) {
      fetchFormData(id);
    } else if (mode === 'draft' && draftId) {
      loadDraftData(draftId);
    }
  }, [mode, id, draftId]);

  const fetchFormData = async (rowId) => {
    try {
      setLoader(true);
      const response = await api.get(`/v1/forms/get_by_id`, { params: { rowid: rowId } });
      if (response.status === 200 && response.data?.length > 0) {
        mapJsonToForm(response.data[0]);
      }
    } catch (error) {
      console.error('Error fetching form data:', error);
    } finally {
      setLoader(false);
    }
  };

  // Load a mobile draft (draft_id is a UUID) and map it like a fetched row.
  const loadDraftData = async (dId) => {
    try {
      setLoader(true);
      const resp = await api.get('/v1/forms/drafts/get', { params: { draft_id: dId } });
      const draft = resp?.draft || null;
      if (draft) {
        notifyLoaded();
        mapJsonToForm(draftToFormRow(draft));
        const savedStep = draft?.form_data?._step;
        if (savedStep) setCurrentStage(savedStep);
      }
    } catch (error) {
      console.error('Error loading draft:', error);
    } finally {
      setLoader(false);
    }
  };

  const mapJsonToForm = (apiData) => {
    if (!apiData || !Array.isArray(apiData.json_data)) return;

    const updated = { ...formData };

    apiData.json_data.forEach((item) => {
      if (!item?.question) return;
      const q = item.question;
      const a = item.answer || '';

      // Assignment
      if (q === 'Name of Person Assigned To') updated.personAssigned = a;
      if (q === 'Trailer Unit #') updated.trailerUnit = a;
      if (q === 'Last Known Job Number') updated.lastKnownJobNumber = a;
      if (q === 'Trailer Type') updated.trailerType = a;

      // Compliance
      if (q === 'Is License Registration Current?') updated.licenseRegistration = a;
      if (q === 'Insurance Card / Current Cards?') updated.insuranceCard = a;
      if (q === 'Does ball size match to trailer hitch size?') updated.ballSizeMatch = a;
      if (q === 'Does your trailer need Fire Extinguisher? (No for utility trailers)') updated.fireExtinguisher = a;

      // Condition
      if (q === 'Tire Condition') updated.tireCondition = a;
      if (q === 'Axel Condition') updated.axelCondition = a;
      if (q === 'Brake Condition') updated.brakeCondition = a;
      if (q === 'Jack and Safety Chain Condition') updated.jackSafetyChain = a;

      // Lights & Safety
      if (q === 'Turn Signal Lights') updated.turnSignalLights = a;
      if (q === 'Brake Lights') updated.brakeLights = a;
      if (q === 'Proper Tie Downs') updated.properTieDowns = a;
      if (q === 'Trailer Hitch and Door Locks') updated.trailerHitchDoorLocks = a;

      // Comments
      if (q === 'Comments - Any Physical Damage') updated.comments = a;
    });

    // Also set top-level fields
    if (apiData.companyid) updated.companyId = apiData.companyid;
    if (apiData.emp_id) updated.empId = apiData.emp_id;
    if (apiData.emp_name) updated.personAssigned = updated.personAssigned || apiData.emp_name;
    if (apiData.job_id) updated.lastKnownJobNumber = updated.lastKnownJobNumber || String(apiData.job_id);

    setFormData(updated);
    if (apiData.companyid) setSelectedCompanyId(apiData.companyid);
  };

  // ─── Validation ───
  const handleNext = () => {
    const validations = {
      1: {
        companyId: 'Please select a Company',
        personAssigned: 'Please select Name of Person Assigned To',
        trailerUnit: 'Please enter Trailer Unit #',
        lastKnownJobNumber: 'Please select Last Known Job Number',
        trailerType: 'Please select Trailer Type',
      },
      2: {
        licenseRegistration: 'Please select License Registration status',
        insuranceCard: 'Please select Insurance Card status',
        ballSizeMatch: 'Please select Ball Size Match status',
        fireExtinguisher: 'Please select Fire Extinguisher status',
      },
      3: {
        tireCondition: 'Please select Tire Condition',
        axelCondition: 'Please select Axel Condition',
        brakeCondition: 'Please select Brake Condition',
        jackSafetyChain: 'Please select Jack and Safety Chain Condition',
      },
      4: {
        turnSignalLights: 'Please select Turn Signal Lights status',
        brakeLights: 'Please select Brake Lights status',
        properTieDowns: 'Please select Proper Tie Downs status',
        trailerHitchDoorLocks: 'Please select Trailer Hitch and Door Locks status',
      },
    };

    const required = validations[currentStage] || {};
    for (let key in required) {
      if (!formData[key]) {
        setSeverity('error');
        setAlertMessage(required[key]);
        setOpen(true);
        return;
      }
    }

    if (currentStage < totalStages) {
      setCurrentStage(currentStage + 1);
    }
  };

  const handlePrevious = () => {
    if (currentStage > 1) setCurrentStage(currentStage - 1);
  };

  // ─── Build JSON data ───
  const generateJsonData = () => {
    return [
      // Assignment
      { question: 'Name of Person Assigned To', answer: formData.personAssigned },
      { question: 'Trailer Unit #', answer: formData.trailerUnit },
      { question: 'Last Known Job Number', answer: formData.lastKnownJobNumber },
      { question: 'Trailer Type', answer: formData.trailerType },

      // Compliance
      { question: 'Is License Registration Current?', answer: formData.licenseRegistration },
      { question: 'Insurance Card / Current Cards?', answer: formData.insuranceCard },
      { question: 'Does ball size match to trailer hitch size?', answer: formData.ballSizeMatch },
      { question: 'Does your trailer need Fire Extinguisher? (No for utility trailers)', answer: formData.fireExtinguisher },

      // Condition
      { question: 'Tire Condition', answer: formData.tireCondition },
      { question: 'Axel Condition', answer: formData.axelCondition },
      { question: 'Brake Condition', answer: formData.brakeCondition },
      { question: 'Jack and Safety Chain Condition', answer: formData.jackSafetyChain },

      // Lights & Safety
      { question: 'Turn Signal Lights', answer: formData.turnSignalLights },
      { question: 'Brake Lights', answer: formData.brakeLights },
      { question: 'Proper Tie Downs', answer: formData.properTieDowns },
      { question: 'Trailer Hitch and Door Locks', answer: formData.trailerHitchDoorLocks },

      // Comments
      { question: 'Comments - Any Physical Damage', answer: formData.comments },
    ];
  };

  // ─── Step-wise draft autosave ───
  const { clearDraft, notifyLoaded } = useFormDraft({
    formName: 'Trailer Inspection Form',
    mode,
    step: currentStage,
    watch: [formData],
    buildSnapshot: () => ({
      json_data: generateJsonData(),
      _step: currentStage,
      companyid: formData.companyId,
      emp_id: formData.empId,
      emp_name: formData.personAssigned,
      job_id: formData.lastKnownJobNumber,
      job_description: selectedJobName,
      method: 'Filled Out',
    }),
  });

  // ─── Submit ───
  const handleSubmit = async () => {
    // Validate comments
    if (!formData.comments) {
      setSeverity('error');
      setAlertMessage('Please enter comments about physical damage');
      setOpen(true);
      return;
    }

    try {
      setLoader(true);
      const today = new Date().toISOString().split('T')[0];
      const jsonData = generateJsonData();

      const payload = {
        emp_id: formData.empId || null,
        emp_name: formData.personAssigned,
        job_id: parseInt(formData.lastKnownJobNumber) || 0,
        job_description: selectedJobName || `Trailer Inspection - Unit #${formData.trailerUnit}`,
        observer_name: user?.firstname || '',
        filled_date: today,
        form_type: 'TRAILER',
        companyid: formData.companyId || null,
        json_data: jsonData,
        documents: '',
        method: 'Filled Out',
      };

      let response;
      if (mode === 'edit' && id) {
        payload.row_id = parseInt(id);
        response = await api.post('/v1/forms/create-update-form', payload);
      } else {
        response = await api.post('/v1/forms/create-form', payload);
      }

      if (response.status === 200) {
        clearDraft();
        setSeverity('success');
        setAlertMessage(mode === 'edit' ? 'Form updated successfully' : 'Form submitted successfully');
        setOpen(true);
        setTimeout(() => navigate('/forms'), 1500);
      } else {
        setSeverity('error');
        setAlertMessage('Something went wrong');
        setOpen(true);
      }
    } catch (error) {
      console.error('Submit error:', error);
      setSeverity('error');
      setAlertMessage('Something went wrong');
      setOpen(true);
    } finally {
      setLoader(false);
    }
  };

  // ─── Render Stepper ───
  const renderStepper = () => (
    <div className="flex items-center justify-center gap-4 py-8">
      {stages.map((stage) => (
        <div key={stage.number} className="flex flex-col items-center gap-1">
          <div
            className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold
              ${
                currentStage > stage.number
                  ? 'bg-[#18181B] text-white'
                  : currentStage === stage.number
                  ? 'bg-[#18181B] text-white'
                  : 'bg-gray-200 text-gray-500'
              }`}
          >
            {currentStage > stage.number ? <FiCheck className="text-lg" /> : stage.number}
          </div>
          <span
            className={`text-xs ${
              currentStage === stage.number ? 'font-bold text-black' : 'text-gray-500'
            }`}
          >
            {stage.label}
          </span>
        </div>
      ))}
    </div>
  );

  // ─── Stage Content ───
  const renderStageContent = () => {
    switch (currentStage) {
      case 1:
        return (
          <div className="border rounded-xl overflow-hidden">
            <div className="bg-[#18181B] text-white text-left px-6 py-3 text-sm font-semibold uppercase">
              ASSIGNMENT
            </div>
            <div className="p-6">
              <h5 className="font-semibold text-left text-gray-800 mb-4">Assignment & Trailer Info</h5>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                {/* Company ID Dropdown */}
                <div className="flex flex-col gap-1">
                  <label className="text-sm text-start font-medium text-gray-700">
                    Company ID <span className="text-red-500">*</span>
                  </label>
                  <select
                    className="border bg-[#F3F3F5] rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-400"
                    value={formData.companyId || ''}
                    onChange={(e) => {
                      const selectedId = e.target.value ? parseInt(e.target.value, 10) : null;
                      setSelectedCompanyId(selectedId);
                      setFormData((prev) => ({ ...prev, companyId: selectedId }));
                    }}
                  >
                    <option value="">Select a Company</option>
                    {companyTabs.map((company) => (
                      <option key={company.id} value={company.id}>
                        {company.name}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Trailer Unit # */}
                <InputField
                  label="Trailer Unit # (Ex: 333)"
                  placeholder="Enter unit number"
                  value={formData.trailerUnit}
                  onChange={(e) => handleInputChange('trailerUnit', e.target.value)}
                  required
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                {/* Employee Search Dropdown */}
                <div className="flex flex-col gap-1 relative" ref={empInputRef}>
                  <label className="text-sm text-start font-medium text-gray-700">
                    Name of Person Assigned To <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    placeholder="Enter employee Id"
                    className="border bg-[#F3F3F5] rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-400"
                    value={formData.personAssigned}
                    onChange={(e) => {
                      const val = e.target.value;
                      handleInputChange('personAssigned', val);
                      handleInputChange('empId', null);
                      const numericVal = val.replace(/\D/g, '');
                      if (!numericVal.trim()) {
                        setEmployeeSuggestions([]);
                        setShowEmpSuggestions(false);
                      } else {
                        fetchEmployeeSuggestions(numericVal);
                      }
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Tab' && employeeSuggestions?.length) {
                        handleEmployeeSelect(employeeSuggestions[0]);
                        e.preventDefault();
                      }
                    }}
                  />
                  {showEmpSuggestions && employeeSuggestions.length > 0 && (
                    <ul className="absolute z-10 top-full mt-1 left-0 text-start bg-white border border-gray-200 rounded-md shadow-lg max-h-[300px] overflow-y-auto text-sm w-full">
                      {employeeSuggestions.map((emp, index) => (
                        <li
                          key={index}
                          onClick={() => handleEmployeeSelect(emp)}
                          onMouseEnter={() => setHoveredIndex(index)}
                          className={`px-3 py-2 cursor-pointer hover:bg-gray-100 ${hoveredIndex === index ? 'bg-gray-100' : ''}`}
                        >
                          {emp.empid} - {emp.firstname} {emp.lastname}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>

                {/* Job Number Search Dropdown */}
                <div className="flex flex-col gap-1 relative" ref={jobInputRef}>
                  <label className="text-sm text-start font-medium text-gray-700">
                    Last Known Job Number <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    placeholder="Enter job number"
                    className="border bg-[#F3F3F5] rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-400"
                    value={formData.lastKnownJobNumber}
                    onChange={(e) => {
                      const val = e.target.value;
                      handleInputChange('lastKnownJobNumber', val);
                      if (!val.trim()) {
                        setSuggestions([]);
                        setShowSuggestions(false);
                        setSelectedJobName('');
                      } else {
                        fetchJobSuggestions(val);
                      }
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Tab' && suggestions?.length) {
                        e.preventDefault();
                        handleJobCodeSelect(suggestions[0]);
                      }
                    }}
                  />
                  {showSuggestions && suggestions.length > 0 && (
                    <ul className="absolute z-10 top-full mt-1 left-0 text-start bg-white border border-gray-200 rounded-md shadow-lg max-h-[300px] overflow-y-auto text-sm w-full">
                      {suggestions.map((code, index) => (
                        <li
                          key={index}
                          onClick={() => handleJobCodeSelect(code)}
                          onMouseEnter={() => setHoveredIndex(index)}
                          className={`px-3 py-2 cursor-pointer hover:bg-gray-100 ${hoveredIndex === index ? 'bg-gray-100' : ''}`}
                        >
                          {code.job_num} - {code.job_name}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>

              <RadioGroup
                label="Trailer Type"
                name="trailerType"
                options={['CT', 'TV', 'TT', 'ET', 'UT']}
                value={formData.trailerType}
                onChange={(val) => handleInputChange('trailerType', val)}
                required
                questionNumber={1}
              />
            </div>
          </div>
        );

      case 2:
        return (
          <div className="border rounded-xl overflow-hidden">
            <div className="bg-[#18181B] text-white text-left px-6 py-3 text-sm font-semibold uppercase">
              COMPLIANCE
            </div>
            <div className="p-6">
              <h5 className="font-semibold text-left text-gray-800 mb-2">Registration & Compliance</h5>

              <RadioGroup
                label="Is License Registration Current?"
                name="licenseRegistration"
                options={['Yes', 'No']}
                value={formData.licenseRegistration}
                onChange={(val) => handleInputChange('licenseRegistration', val)}
                required
                questionNumber={2}
              />
              <RadioGroup
                label="Insurance Card / Current Cards?"
                name="insuranceCard"
                options={['Yes', 'No']}
                value={formData.insuranceCard}
                onChange={(val) => handleInputChange('insuranceCard', val)}
                required
                questionNumber={3}
              />
              <RadioGroup
                label="Does ball size match to trailer hitch size?"
                name="ballSizeMatch"
                options={['Yes', 'No']}
                value={formData.ballSizeMatch}
                onChange={(val) => handleInputChange('ballSizeMatch', val)}
                required
                questionNumber={4}
              />
              <RadioGroup
                label="Does your trailer need Fire Extinguisher? (No for utility trailers)"
                name="fireExtinguisher"
                options={['Yes', 'No']}
                value={formData.fireExtinguisher}
                onChange={(val) => handleInputChange('fireExtinguisher', val)}
                required
                questionNumber={5}
              />
            </div>
          </div>
        );

      case 3:
        return (
          <div className="border rounded-xl overflow-hidden">
            <div className="bg-[#18181B] text-white text-left px-6 py-3 text-sm font-semibold uppercase">
              CONDITION
            </div>
            <div className="p-6">
              <h5 className="font-semibold text-left text-gray-800 mb-4">Physical Condition Assessment</h5>

              <RadioGroup
                label="Tire Condition"
                name="tireCondition"
                options={['Good', 'Fair', 'Poor']}
                value={formData.tireCondition}
                onChange={(val) => handleInputChange('tireCondition', val)}
                required
                questionNumber={6}
              />
              <RadioGroup
                label="Axel Condition"
                name="axelCondition"
                options={['Good', 'Fair', 'Poor']}
                value={formData.axelCondition}
                onChange={(val) => handleInputChange('axelCondition', val)}
                required
                questionNumber={7}
              />
              <RadioGroup
                label="Brake Condition"
                name="brakeCondition"
                options={['Good', 'Fair', 'Poor']}
                value={formData.brakeCondition}
                onChange={(val) => handleInputChange('brakeCondition', val)}
                required
                questionNumber={8}
              />
              <RadioGroup
                label="Jack and Safety Chain Condition"
                name="jackSafetyChain"
                options={['Good', 'Fair', 'Poor']}
                value={formData.jackSafetyChain}
                onChange={(val) => handleInputChange('jackSafetyChain', val)}
                required
                questionNumber={9}
              />
            </div>
          </div>
        );

      case 4:
        return (
          <div className="border rounded-xl overflow-hidden">
            <div className="bg-[#18181B] text-white text-left px-6 py-3 text-sm font-semibold uppercase">
              LIGHTS & SAFETY
            </div>
            <div className="p-6">
              <h5 className="font-semibold text-left text-gray-800 mb-4">Lights & Safety Check</h5>

              <RadioGroup
                label="Turn Signal Lights"
                name="turnSignalLights"
                options={['Yes', 'No']}
                value={formData.turnSignalLights}
                onChange={(val) => handleInputChange('turnSignalLights', val)}
                required
                questionNumber={10}
              />
              <RadioGroup
                label="Brake Lights"
                name="brakeLights"
                options={['Yes', 'No']}
                value={formData.brakeLights}
                onChange={(val) => handleInputChange('brakeLights', val)}
                required
                questionNumber={11}
              />
              <RadioGroup
                label="Proper Tie Downs"
                name="properTieDowns"
                options={['Yes', 'No']}
                value={formData.properTieDowns}
                onChange={(val) => handleInputChange('properTieDowns', val)}
                required
                questionNumber={12}
              />
              <RadioGroup
                label="Trailer Hitch and Door Locks"
                name="trailerHitchDoorLocks"
                options={['Yes', 'No']}
                value={formData.trailerHitchDoorLocks}
                onChange={(val) => handleInputChange('trailerHitchDoorLocks', val)}
                required
                questionNumber={13}
              />
            </div>
          </div>
        );

      case 5:
        return (
          <div className="border rounded-xl overflow-hidden">
            <div className="bg-[#18181B] text-white text-left px-6 py-3 text-sm font-semibold uppercase">
              COMMENTS
            </div>
            <div className="p-6">
              <h5 className="font-semibold text-left text-gray-800 mb-4">Additional Comments</h5>

              <div className="flex flex-col gap-1 mb-4">
                <label className="text-sm text-left font-medium text-gray-700">
                  Comments - Any Physical Damage <span className="text-red-500">*</span>
                </label>
                <textarea
                  placeholder="Describe any physical damage or additional notes..."
                  value={formData.comments || ''}
                  onChange={(e) => handleInputChange('comments', e.target.value)}
                  rows={3}
                  className="rounded-md px-3 py-2 text-sm placeholder:text-[#717182] focus:outline-none focus:ring-2 focus:ring-gray-400 bg-[#F3F3F5] resize-none"
                />
              </div>

              <div className="bg-[#eff1f2] shadow-sm border border-[#E5E7EB] text-left rounded-md p-4 text-xs text-[#6A7282]">
                By submitting this form, you confirm that the information provided is accurate and
                complete to the best of your knowledge.
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 py-6">
        {/* Header */}
        <div className="flex items-center gap-3 mb-2">
          <button
            onClick={() => navigate('/forms')}
            className="text-gray-600 hover:text-black"
          >
            <FiArrowLeft className="text-xl" />
          </button>
          <h1 className="text-2xl font-bold text-gray-900">TRAILER INSPECTION FORM</h1>
        </div>

        {/* Stepper */}
        {renderStepper()}

        {/* Stage Content */}
        {renderStageContent()}

        {/* Navigation Buttons */}
        <div className="flex justify-between mt-6 mb-10">
          {currentStage > 1 ? (
            <button
              onClick={handlePrevious}
              className="flex items-center gap-2 px-6 py-2 border border-gray-300 rounded-lg text-sm text-gray-600 hover:bg-gray-100"
            >
              &lt; Previous
            </button>
          ) : (
            <div />
          )}

          {currentStage < totalStages ? (
            <button
              onClick={handleNext}
              className="flex items-center gap-2 px-6 py-2 bg-[#18181B] text-white rounded-lg text-sm hover:bg-gray-800"
            >
              Next &gt;
            </button>
          ) : (
            <button
              onClick={handleSubmit}
              className="flex items-center gap-2 px-6 py-2 bg-[#18181B] text-white rounded-lg text-sm hover:bg-gray-800"
            >
              Submit
            </button>
          )}
        </div>
      </div>

      <Backdrop sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }} open={loader}>
        <CircularProgress color="inherit" />
      </Backdrop>

      <ToastMessage
        message={alertMessage}
        severity={severity}
        open={open}
        handleClose={handleCloseToast}
      />
    </div>
  );
};

export default TrailerInspectionForm;
